// Phase A.2.b BLOCKER 2 · Unified People-linked-to-Client workspace.
//
// - Renders relationships bidirectionally:
//     mode="client":   given customerId, shows the People linked to that Client
//     mode="contact":  given contactId,  shows the Clients linked to that Contact
//
// - Link workflow uses <ContactLinker mode="client">. Global search + create.
// - Row actions: View, Call (tel:), Email (mailto:), Make Primary /
//   ★ Primary, Change Role, Unlink.
// - Historic role vocabulary DISPLAYS via `roleLabel()`; edits force a
//   canonical role.
// - Primary write-through: `PATCH /api/client-contact-relationships/{id}
//   { is_primary: true|false }` — backend atomically demotes any
//   previous Primary on the same Client so "at most one Primary per
//   Client" is invariant. Never touches CROC rows.

import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import { Link2, X, Loader2, Mail, Phone, ExternalLink, Star, Edit3, Tag } from "lucide-react";
import { clientContactRelApi } from "@/lib/entities";
import { useAuth } from "@/contexts/AuthContext";
import { CANONICAL_ROLES, roleLabel } from "@/lib/relationshipRoles";
import { displayName as safeName } from "@/lib/contactDisplay";
import { bestPhone, telHref } from "@/lib/contactPhone";
import { ContactLinker } from "@/components/ContactLinker";
import { RoleChangeDialog } from "@/components/RoleChangeDialog";
import { ContactFormDialog } from "@/pages/contacts/ContactFormDialog";

export function ClientContactRelationshipsPanel({ mode, customerId, contactId }) {
  const { can } = useAuth();
  const canEdit = can("contacts.edit");
  const [rows, setRows] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [roleDlg, setRoleDlg] = useState(null); // {row}
  const [editDlg, setEditDlg] = useState(null); // contact object
  const navigate = useNavigate();

  const load = async () => {
    try {
      const d =
        mode === "client"
          ? await clientContactRelApi.forClient(customerId)
          : await clientContactRelApi.forContact(contactId);
      // Sort so Primary bubbles to the top on client-view.
      if (mode === "client" && Array.isArray(d)) {
        d.sort((a, b) => {
          if ((b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0) !== 0) {
            return (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0);
          }
          const an = safeName(a.contact).toLowerCase();
          const bn = safeName(b.contact).toLowerCase();
          return an.localeCompare(bn);
        });
      }
      setRows(d);
    } catch {
      toast.error("Failed to load relationships");
      setRows([]);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, contactId, mode]);

  const changeRole = async (id, role) => {
    try {
      await clientContactRelApi.patch(id, { role });
      toast.success("Role updated.");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409) {
        toast.error("This Person is already linked to this Client with that role.");
        await load();
      } else if (e?.response?.status === 422 && detail?.code === "invalid_role") {
        toast.error(`"${roleLabel(detail.given)}" is not a valid role.`);
      } else {
        toast.error("Update failed.");
      }
    }
  };

  const togglePrimary = async (row) => {
    const nextPrimary = !row.is_primary;
    try {
      await clientContactRelApi.patch(row.id, { is_primary: nextPrimary });
      toast.success(nextPrimary ? "Primary set." : "Primary removed.");
      await load();
    } catch {
      toast.error("Primary update failed.");
    }
  };

  const unlink = async (id) => {
    if (
      !window.confirm(
        "Unlink this relationship? The Person and Client both remain.",
      )
    )
      return;
    try {
      await clientContactRelApi.unlink(id);
      toast.success("Unlinked.");
      await load();
    } catch {
      toast.error("Unlink failed.");
    }
  };

  const title = mode === "client" ? "People" : "Clients";
  const linkLabel = mode === "client" ? "Link Person" : "Link Client";

  return (
    <div
      className="bg-white border border-coach-silver rounded-md"
      data-testid={`ccr-panel-${mode}`}
    >
      <div className="px-4 py-2 border-b border-coach-silver flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-steel">
          {title}
        </div>
        {canEdit && mode === "client" && (
          <button
            onClick={() => setLinkOpen(true)}
            data-testid="btn-link-contact"
            className="inline-flex items-center gap-1 text-[12px] bg-coach-blue text-white px-2 py-1 rounded-sm hover:bg-deep-road-navy focus-visible:outline-2 focus-visible:outline-touring-lime"
          >
            <Link2 size={12} /> {linkLabel}
          </button>
        )}
      </div>
      {rows === null && (
        <div className="px-4 py-3 text-[12px] text-steel inline-flex items-center gap-1">
          <Loader2 size={12} className="animate-spin" /> Loading…
        </div>
      )}
      {rows && rows.length === 0 && (
        <div
          className="px-4 py-6 text-[13px] text-steel"
          data-testid={`ccr-empty-${mode}`}
        >
          {mode === "client"
            ? "No People linked to this Client yet."
            : "This Person is not linked to any Clients yet."}
        </div>
      )}
      {rows && rows.length > 0 && (
        <ul className="divide-y divide-coach-silver/60">
          {rows.map((r) => (
            <RelationshipRow
              key={r.id}
              row={r}
              mode={mode}
              customerId={customerId}
              canEdit={canEdit}
              onOpenChangeRole={(row) => setRoleDlg({ row })}
              onEditPerson={(c) => setEditDlg(c)}
              onTogglePrimary={togglePrimary}
              onUnlink={unlink}
              onView={(cid) =>
                navigate(mode === "client" ? `/contacts/${cid}` : `/customers/${cid}`)
              }
            />
          ))}
        </ul>
      )}
      {linkOpen && (
        <ContactLinker
          mode="client"
          customerId={customerId}
          onClose={() => setLinkOpen(false)}
          onLinked={async (opts) => {
            await load();
            if (!opts?.silent) setLinkOpen(false);
          }}
        />
      )}
      {roleDlg && (
        <RoleChangeDialog
          open={!!roleDlg}
          currentRole={roleDlg.row.role}
          personName={
            mode === "client"
              ? safeName(roleDlg.row.contact)
              : roleDlg.row.client?.display_name || "Relationship"
          }
          onClose={() => setRoleDlg(null)}
          onSave={async (newRole) => {
            await changeRole(roleDlg.row.id, newRole);
            setRoleDlg(null);
          }}
          testidPrefix="ccr-role"
        />
      )}
      {editDlg && (
        <ContactFormDialog
          open={!!editDlg}
          editing={editDlg}
          onClose={() => setEditDlg(null)}
          onSaved={async () => {
            setEditDlg(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function RelationshipRow({
  row, mode, customerId, canEdit, onOpenChangeRole, onEditPerson,
  onTogglePrimary, onUnlink, onView,
}) {
  if (mode === "contact") {
    return (
      <li
        className="px-4 py-3 flex items-start justify-between gap-2"
        data-testid={`ccr-row-${row.id}`}
      >
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-medium text-carbon truncate">
            <a
              href={`/customers/${row.customer_id}`}
              className="text-coach-blue hover:underline inline-flex items-center gap-1"
            >
              {row.client?.display_name || "(missing client)"}
              <ExternalLink size={11} aria-hidden="true" />
            </a>
            {row.is_primary && (
              <span
                className="ml-2 text-[10.5px] uppercase tracking-widest text-coach-blue border border-coach-blue/40 rounded-sm px-1.5 py-0.5"
                data-testid={`ccr-primary-badge-${row.id}`}
              >
                Primary
              </span>
            )}
          </div>
          <div className="text-[11.5px] text-steel">
            Role: {roleLabel(row.role)}
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => onOpenChangeRole(row)}
              className="inline-flex items-center gap-1 border border-coach-silver text-[11.5px] text-carbon px-2 py-1 rounded-sm hover:bg-road-white focus-visible:outline-2 focus-visible:outline-coach-blue"
              data-testid={`ccr-change-role-${row.id}`}
              aria-label="Change role"
            >
              <Tag size={11} /> Change Role
            </button>
            <button
              onClick={() => onUnlink(row.id)}
              data-testid={`ccr-unlink-${row.id}`}
              className="text-critical-red hover:text-critical-red/80"
              title="Unlink"
              aria-label="Unlink"
            >
              <X size={14} />
            </button>
          </div>
        )}
      </li>
    );
  }

  // Client→People view.
  const c = row.contact || {};
  const isMissing = Boolean(c?.missing);
  const isArchived = !isMissing && Boolean(c?.is_archived);
  const name = isMissing ? "(Missing Contact)" : safeName(c);
  const phone = isMissing ? null : bestPhone(c);
  const phoneHref = isMissing ? null : telHref(phone);
  return (
    <li
      className="px-4 py-3 flex flex-wrap sm:flex-nowrap items-start gap-3"
      data-testid={`ccr-row-${row.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-medium text-carbon truncate">
          {row.is_primary && !isMissing && (
            <span
              className="mr-2 text-[10.5px] uppercase tracking-widest text-coach-blue border border-coach-blue/40 rounded-sm px-1.5 py-0.5"
              data-testid={`ccr-primary-badge-${row.id}`}
            >
              ★ Primary
            </span>
          )}
          {isMissing ? (
            <span
              className="text-steel"
              data-testid={`ccr-missing-contact-${row.id}`}
              aria-label="Missing Contact"
            >
              {name}
            </span>
          ) : (
            <>
              {/* RC10a · active AND archived Contact names are proper
                  accessible links to their canonical Contact/Person
                  detail page. Route uses `row.contact_id` (never the
                  display name or email). Return context (Client id +
                  Contacts tab + scroll position) is persisted in
                  sessionStorage so browser Back returns to the same
                  panel with the tab active. */}
              <Link
                to={`/contacts/${row.contact_id}`}
                onClick={() => {
                  if (!customerId) return;
                  try {
                    sessionStorage.setItem(
                      `client:${customerId}:returnCtx`,
                      JSON.stringify({
                        tab: "contacts",
                        scrollY: window.scrollY || 0,
                        ts: Date.now(),
                      }),
                    );
                  } catch {
                    /* sessionStorage may be unavailable — silent no-op. */
                  }
                }}
                className="text-carbon hover:text-coach-blue underline decoration-transparent hover:decoration-current underline-offset-2 focus-visible:outline-2 focus-visible:outline-coach-blue focus-visible:outline-offset-2 rounded-sm cursor-pointer"
                data-testid={`ccr-contact-name-link-${row.id}`}
                aria-label={`Open ${name} contact detail`}
              >
                {name}
              </Link>
              {isArchived && (
                <span
                  className="ml-2 text-[10px] uppercase tracking-widest text-amber-hold border border-amber-hold/40 rounded-sm px-1.5 py-0.5"
                  data-testid={`ccr-archived-badge-${row.id}`}
                  aria-label="Archived contact"
                  title="This Contact is archived. Historical relationship remains visible; not eligible for new selectors."
                >
                  Archived
                </span>
              )}
              {c.title && (
                <span className="ml-2 text-[11.5px] font-normal text-steel">
                  · {c.title}
                </span>
              )}
            </>
          )}
        </div>
        {!isMissing && (
          <div className="text-[11.5px] text-steel truncate">
            <span className="uppercase tracking-widest text-[10.5px] mr-2">
              {roleLabel(row.role)}
            </span>
            {c.primary_email || "—"}
            {c.mobile ? ` · ${c.mobile}` : ""}
          </div>
        )}
        {/* RC10 Phase 2A · relationship-scoped metadata surfaced
             beneath the person line. Empty values are hidden. */}
        {(row.job_title || row.department || row.is_billing_contact) && (
          <div
            className="text-[11.5px] text-steel truncate mt-0.5 flex flex-wrap gap-x-2"
            data-testid={`ccr-relmeta-${row.id}`}
          >
            {row.job_title && <span>{row.job_title}</span>}
            {row.department && <span>· {row.department}</span>}
            {row.is_billing_contact && (
              <span
                className="text-coach-blue"
                data-testid={`ccr-billing-badge-${row.id}`}
              >
                · Billing Contact
              </span>
            )}
          </div>
        )}
        {!isMissing && c.primary_organization_name && (
          <div className="text-[11.5px] text-steel truncate">
            {c.primary_organization_name}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 shrink-0">
        {isMissing ? null : (
        <>
        {canEdit && (
          <button
            type="button"
            onClick={() => onTogglePrimary(row)}
            title={row.is_primary ? "Remove Primary" : "Make Primary"}
            aria-label={row.is_primary ? "Remove Primary" : "Make Primary"}
            data-testid={`ccr-primary-toggle-${row.id}`}
            className={`${row.is_primary ? "text-coach-blue" : "text-steel hover:text-coach-blue"} rounded-sm focus-visible:outline-2 focus-visible:outline-coach-blue`}
          >
            <Star
              size={14}
              fill={row.is_primary ? "currentColor" : "none"}
              aria-hidden="true"
            />
          </button>
        )}
        {c.id && (
          <button
            type="button"
            onClick={() => onView(c.id)}
            data-testid={`ccr-view-person-${row.id}`}
            className="inline-flex items-center gap-1 text-[11.5px] text-coach-blue hover:underline rounded-sm focus-visible:outline-2 focus-visible:outline-coach-blue"
            aria-label={`View ${name}`}
          >
            <ExternalLink size={11} /> View Person
          </button>
        )}
        {canEdit && c.id && (
          <button
            type="button"
            onClick={() => onEditPerson(c)}
            data-testid={`ccr-edit-person-${row.id}`}
            className="inline-flex items-center gap-1 text-[11.5px] text-coach-blue hover:underline rounded-sm focus-visible:outline-2 focus-visible:outline-coach-blue"
            aria-label={`Edit ${name}`}
          >
            <Edit3 size={11} /> Edit Person
          </button>
        )}
        {c.primary_email && (
          <a
            href={`mailto:${c.primary_email}`}
            className="text-coach-blue hover:text-deep-road-navy"
            title={`Email ${name}`}
            aria-label={`Email ${name}`}
            data-testid={`ccr-email-${row.id}`}
          >
            <Mail size={14} />
          </a>
        )}
        {phoneHref && (
          <a
            href={phoneHref}
            className="lg:hidden text-coach-blue hover:text-deep-road-navy"
            title={`Call ${name}`}
            aria-label={`Call ${name}`}
            data-testid={`ccr-call-${row.id}`}
          >
            <Phone size={14} />
          </a>
        )}
        {canEdit && (
          <button
            type="button"
            onClick={() => onOpenChangeRole(row)}
            className="inline-flex items-center gap-1 border border-coach-silver text-[11.5px] text-carbon px-2 py-1 rounded-sm hover:bg-road-white focus-visible:outline-2 focus-visible:outline-coach-blue"
            data-testid={`ccr-change-role-${row.id}`}
            aria-label="Change role"
          >
            <Tag size={11} /> Change Role
          </button>
        )}
        {canEdit && (
          <button
            onClick={() => onUnlink(row.id)}
            data-testid={`ccr-unlink-${row.id}`}
            className="text-critical-red hover:text-critical-red/80"
            title="Unlink Person"
            aria-label={`Unlink ${name}`}
          >
            <X size={14} />
          </button>
        )}
        </>
        )}
      </div>
    </li>
  );
}

function RolePicker() { return null; } // deprecated; kept for import compatibility.

export default ClientContactRelationshipsPanel;
